function plainText(value) {
  if (Array.isArray(value)) {
    return value.map((item) => plainText(item)).filter(Boolean).join(', ');
  }
  if (value === undefined || value === null || value === '') return '';
  return String(value);
}

function truthyText(value) {
  const text = plainText(value).trim();
  return text && text !== 'N/A' ? text : '';
}

function formatLeadConfidence(value) {
  if (value === undefined || value === null || value === '') return '';
  if (typeof value === 'boolean') return '';
  const number = Number(value);
  if (!Number.isFinite(number)) return truthyText(value);
  if (number >= 0 && number <= 1) return `${Math.round(number * 100)}%`;
  if (number > 1 && number <= 100) return `${Math.round(number)}%`;
  return String(number);
}

function leadEvidenceText(item) {
  if (typeof item === 'string') return truthyText(item);
  if (!item || typeof item !== 'object') return '';

  const title = truthyText(item.title);
  const url = truthyText(item.url);
  const text = truthyText(item.text);
  const source = truthyText(item.source);
  if (title && url) return `${title} ${url}`;
  if (text && url) return `${text} ${url}`;
  if (title && text) return `${title}: ${text}`;
  return text || title || url || source;
}

function normalizeLeadContact(contact) {
  if (!contact || typeof contact !== 'object') return null;
  const evidence = Array.isArray(contact.evidence)
    ? contact.evidence.map(leadEvidenceText).filter(Boolean)
    : [];
  const normalized = {
    name: truthyText(contact.name),
    title: truthyText(contact.title),
    email: truthyText(contact.email),
    phone: truthyText(contact.phone),
    linkedinUrl: truthyText(contact.linkedin_url || contact.linkedinUrl),
    confidence: formatLeadConfidence(contact.confidence),
    source: truthyText(contact.source),
    evidence,
    ...(contact.field_status ? {fieldStatus: contact.field_status} : {}),
  };
  if (
    normalized.name ||
    normalized.title ||
    normalized.email ||
    normalized.phone ||
    normalized.linkedinUrl ||
    normalized.confidence ||
    normalized.source ||
    normalized.evidence.length > 0
  ) {
    return normalized;
  }
  return null;
}

function candidateLeadContacts(candidate) {
  if (!Array.isArray(candidate?.lead_finder_contacts)) return [];
  return candidate.lead_finder_contacts.map(normalizeLeadContact).filter(Boolean);
}

function hasLeadEnrichment(candidate) {
  return Boolean(
    candidate?.lead_finder_enriched_at ||
      (Array.isArray(candidate?.lead_finder_contacts) && candidate.lead_finder_contacts.length > 0)
  );
}

export function buildLeadSummary(sampleCandidates, rowForCandidate) {
  const enrichedCandidates = sampleCandidates.filter(hasLeadEnrichment);
  const companies = [];
  let noContactCompanyCount = 0;

  enrichedCandidates.forEach((candidate) => {
    const contacts = candidateLeadContacts(candidate);
    if (contacts.length === 0) {
      noContactCompanyCount += 1;
      return;
    }
    const row = rowForCandidate(candidate);
    companies.push({
      companyId: row.id,
      companyName: row.business_name,
      contacts,
    });
  });

  if (enrichedCandidates.length === 0 && companies.length === 0) return null;
  return {
    enrichedCompanyCount: enrichedCandidates.length,
    contactCompanyCount: companies.length,
    noContactCompanyCount,
    companies,
  };
}

export function buildLeadLockedHint(leadSummary, sampleCount, totalCount, artifact) {
  if (artifact?.owner_run_completed === true) return null;
  const lockedCount = (totalCount ?? sampleCount) - sampleCount;
  if (!leadSummary || lockedCount <= 0) return null;
  return (
    `Lead contacts cover the ${sampleCount} preview companies. ` +
    `Run Enrich list to find leads for the remaining ${lockedCount}.`
  );
}

const LEAD_JOB_TYPES = new Set(['artifact_lead_enrichment', 'artifact_lead_sample_enrichment']);

// FE #807: history shares stored contacts across turns but projects columns per turn.
// A discovery turn hands its contacts to a later lead-job turn on the same artifact;
// without such a turn (legacy in-place runs) the contacts stay on the discovery card.
export function leadContactsBelongToLaterTurn(message, index, history) {
  if (message?.job_type !== 'target_list_discovery' || !Array.isArray(history)) return false;
  const artifactId = message.artifact_id || message.artifact?.artifact_id;
  return history.slice(index + 1).some((later) => LEAD_JOB_TYPES.has(later?.job_type)
    && (later.artifact_id || later.artifact?.artifact_id) === artifactId);
}

function companyCount(count) {
  return `${count} ${count === 1 ? 'company' : 'companies'}`;
}

// A single-row run reads "No verified lead found"; larger runs name how many rows.
function forRows(count, total) {
  return total > 1 ? ` for ${companyCount(count)}` : '';
}

/**
 * Outcome copy for a finished chat lead job, from lead_outcome v1 rows
 * (row_outcome_summary.rows). The free sample never carries charge wording.
 * Failed jobs keep their backend status_message; other job types return null.
 */
export function leadJobOutcomeNotice(job) {
  if (!LEAD_JOB_TYPES.has(job?.job_type)) return null;
  const paid = job.job_type === 'artifact_lead_enrichment';
  const rows = Array.isArray(job.row_outcome_summary?.rows) ? job.row_outcome_summary.rows : [];
  const failedLookupCharge = job.status === 'failed' && paid && rows.some((row) =>
    row?.lead_outcome?.charged === true && row.lead_outcome.charge_reason === 'explorium_lookup');
  if (job.status !== 'done' && !failedLookupCharge) return null;
  const notCharged = paid ? ' — not charged' : '';
  const counts = {verified_lead_found: 0, no_verified_lead: 0, provider_outage: 0};
  const chargedCounts = {no_verified_lead: 0, provider_outage: 0};
  const chargedCredits = {no_verified_lead: 0, provider_outage: 0};
  rows.forEach((row) => {
    const outcome = row?.lead_outcome?.outcome;
    if (paid && outcome in chargedCounts && row.lead_outcome.charged === true) {
      chargedCounts[outcome] += 1;
      chargedCredits[outcome] += Number(row.lead_outcome.credits_charged) || 0;
    } else if (outcome in counts) counts[outcome] += 1;
  });
  if (!rows.length && job.status_reason === 'no_verified_leads_refunded') counts.no_verified_lead = 1;
  const total = counts.verified_lead_found + counts.no_verified_lead + counts.provider_outage
    + chargedCounts.no_verified_lead + chargedCounts.provider_outage;
  if (!total) return null;
  const parts = [];
  if (counts.verified_lead_found) parts.push(`Verified lead found${forRows(counts.verified_lead_found, total)}.`);
  if (counts.no_verified_lead) parts.push(`No verified lead found${forRows(counts.no_verified_lead, total)}${notCharged}.`);
  if (counts.provider_outage) {
    parts.push(`Lead search was unavailable${forRows(counts.provider_outage, total)}${notCharged}. Try again.`);
  }
  Object.entries(chargedCounts).forEach(([outcome, count]) => {
    if (!count) return;
    const label = outcome === 'no_verified_lead' ? 'No verified lead found' : 'Lead search was unavailable';
    parts.push(`${label}${forRows(count, total)} — charged ${chargedCredits[outcome]} credits for contact provider lookup.`);
  });
  return {
    message: parts.join(' '),
    severity: counts.no_verified_lead || counts.provider_outage || chargedCounts.no_verified_lead
      || chargedCounts.provider_outage ? 'warning' : 'success',
  };
}
